import React, { useRef, useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { Canvg } from 'canvg';

import { Bar, Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import Document from '../../PDFReport';
import Server from '../../../services/Server';
import ViewersMap from '../../../components/Event/ViewersMap';
import getColorForChart from '../../../services/chartColors';
import removeAccents from '../../../services/removeAccents';
import ModulePanel from '../../ModulePanel';
import { getFormattedData } from '../utils';

import chartHoursComplement from './chartHoursComplement.json';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

// Corporate theme colors
const THEME_COLORS = {
  primary: '#336858',
  primaryLight: 'rgba(51, 104, 88, 0.2)',
  primaryHover: 'rgba(51, 104, 88, 0.4)',
};

// Chart default data structure
const defaultChartData = { labels: [], datasets: [] };

// Chart base configuration
const chartDefaultData = {
  labels: [],
  datasets: [
    {
      label: '',
      backgroundColor: THEME_COLORS.primaryLight,
      borderColor: THEME_COLORS.primary,
      borderWidth: 1,
      hoverBackgroundColor: THEME_COLORS.primaryHover,
      hoverBorderColor: THEME_COLORS.primary,
      data: [],
    },
  ],
};

// Bar chart options
const barChartOptions = {
  indexAxis: 'y',
  animation: false,
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    datalabels: {
      anchor: 'end',
      align: 'end',
      font: { size: 14, weight: 'bold' },
      color: '#2d3748',
    },
    legend: { display: false },
  },
  layout: {
    padding: { left: 10, right: 50, top: 10, bottom: 10 },
  },
  scales: {
    y: {
      ticks: { font: { size: 12 }, color: '#2d3748' },
      grid: { display: false },
    },
    x: {
      beginAtZero: true,
      ticks: {
        font: { size: 10 },
        color: '#4a5568',
        callback: (value) => (Number.isInteger(value) ? value : ''),
      },
      grid: { color: '#e2e8f0' },
    },
  },
};

// Line chart options
const lineChartOptions = {
  animation: false,
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    datalabels: { display: false },
    legend: { display: false },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        font: { size: 10 },
        color: '#4a5568',
        callback: (value) => (Number.isInteger(value) ? value : ''),
      },
      grid: { color: '#e2e8f0' },
    },
    x: {
      ticks: {
        font: { size: 9 },
        color: '#4a5568',
        maxRotation: 45,
        minRotation: 45,
      },
      grid: { display: false },
    },
  },
};

// Pie chart options - maintainAspectRatio must be true for circular pie charts
const pieChartOptions = {
  animation: false,
  responsive: true,
  maintainAspectRatio: true,
  aspectRatio: 1.5, // Slightly wider to accommodate legend
  plugins: {
    datalabels: { display: false },
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        font: { size: 12 },
        color: '#2d3748',
        padding: 12,
        usePointStyle: true,
        pointStyle: 'circle',
      },
    },
  },
};

// Loading steps
const LOADING_STEPS = [
  { key: 'stats', label: 'Cargando estadísticas' },
  { key: 'charts', label: 'Generando gráficos' },
  { key: 'conversations', label: 'Cargando conversaciones' },
  { key: 'document', label: 'Preparando documento' },
];

const Report = (props) => {
  const { event, firebase, currentUser, eventImage } = props;

  // Chart refs
  const citiesChartRef = useRef(null);
  const countriesChartRef = useRef(null);
  const hoursChartRef = useRef(null);
  const mapRef = useRef(null);
  const mapCanvasRef = useRef(null);
  const browsersChartRef = useRef(null);
  const osChartRef = useRef(null);

  // Chart data states
  const [citiesData, setCitiesData] = useState(defaultChartData);
  const [countriesData, setCountriesData] = useState(defaultChartData);
  const [hoursData, setHoursData] = useState(defaultChartData);
  const [browsersData, setBrowsersData] = useState(defaultChartData);
  const [osData, setOsData] = useState(defaultChartData);

  // Chart image states
  const [chartImages, setChartImages] = useState({
    c1: null,
    c2: null,
    c3: null,
    c4: null,
    c7: null,
    c8: null,
  });

  // Data states
  const [countriesMap, setCountriesMap] = useState({});
  const [rawCountries, setRawCountries] = useState([]);
  const [countryDescription, setCountryDescription] = useState({});
  const [hoursDescription, setHoursDescription] = useState({});
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalConcurrences, setTotalConcurrences] = useState(0);
  const [chats, setChats] = useState([]);
  const [forums, setForums] = useState([]);

  // UI states
  const [noData, setNoData] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isDataReady, setIsDataReady] = useState(false);
  const [isChartsReady, setIsChartsReady] = useState(false);
  const [isDocumentReady, setIsDocumentReady] = useState(false);
  const [error, setError] = useState(null);

  // Format bar chart data
  const formatBarData = useCallback((items, maxItems = 10) => {
    const sorted = [...items].sort((a, b) => b.value - a.value).slice(0, maxItems);
    return {
      labels: sorted.map((item) => item.label),
      datasets: [
        {
          ...chartDefaultData.datasets[0],
          data: sorted.map((item) => item.value),
        },
      ],
    };
  }, []);

  // Format pie chart data
  const formatPieData = useCallback((items) => {
    const labels = [];
    const data = [];
    const colors = [];
    const hoverColors = [];

    items.forEach((item, index) => {
      labels.push(item.label);
      data.push(item.value);
      colors.push(getColorForChart(index, 0.8));
      hoverColors.push(getColorForChart(index, 1));
    });

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors,
          hoverBackgroundColor: hoverColors,
        },
      ],
    };
  }, []);

  // Fetch statistics data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setCurrentStep(0);
        const response = await Server.events.getStatistics(event.id, 'report');

        if (response.status === 204) {
          setNoData(true);
          return;
        }

        if (response.status !== 200) {
          throw new Error('Error al cargar estadísticas');
        }

        const formattedData = getFormattedData(response.data, true);
        const {
          browsers,
          cities,
          countries,
          hours,
          os,
          total_viewers,
          viewers_peak,
        } = formattedData;

        setTotalUsers(total_viewers);
        setTotalConcurrences(viewers_peak);

        // Format chart data
        setCitiesData(formatBarData(cities));
        setCountriesData(formatBarData(countries));
        setBrowsersData(formatPieData(browsers));
        setOsData(formatPieData(os));

        // Format hours data
        const hoursChartData = {
          labels: hours.labels || [],
          datasets: [
            {
              ...chartDefaultData.datasets[0],
              ...chartHoursComplement,
              fill: true,
              backgroundColor: THEME_COLORS.primaryLight,
              borderColor: THEME_COLORS.primary,
              data: hours.data || [],
            },
          ],
        };
        setHoursData(hoursChartData);

        // Build countries map for the world map
        const countryCodes = {};
        countries.forEach((country) => {
          countryCodes[country.code] = country.value;
        });
        setCountriesMap(countryCodes);
        setRawCountries(countries.slice(0, 10));

        // Build descriptions
        setCountryDescription({
          firstCountry: countries[0]?.label || '',
          firstCity: cities[0]?.label || '',
          secondCountries: countries.slice(1, 3).map((c) => c.label),
        });

        setHoursDescription({
          maxHour: hours.maxHour || '',
          maxConcurrence: hours.maxConcurrence || 0,
        });

        // Fetch chat and forum data in parallel
        setCurrentStep(2);
        await Promise.all([
          fetchChats(),
          fetchForums(),
        ]);

        setIsDataReady(true);
      } catch (err) {
        console.error('Error loading report data:', err);
        setError(err.message);
        setNoData(true);
      }
    };

    const fetchChats = async () => {
      try {
        const refChats = firebase.getRoom('chats', event.id, 'messages');
        const response = await refChats
          .orderBy('timestamp', 'asc')
          .limit(100)
          .get();

        if (!response.empty) {
          const messages = [];
          response.forEach((doc) => {
            const data = doc.data();
            messages.push({
              ...data,
              id: doc.id,
              dateLabel: moment
                .unix(data.timestamp?.seconds)
                .format('YYYY-MM-DD HH:mm:ss'),
            });
          });
          setChats(messages);
        }
      } catch (err) {
        console.error('Error fetching chats:', err);
      }
    };

    const fetchForums = async () => {
      try {
        const refForums = firebase.getRoom('forums', event.id, 'posts');
        const response = await refForums
          .orderBy('timestamp', 'asc')
          .limit(100)
          .get();

        if (!response.empty) {
          const posts = [];
          response.forEach((doc) => {
            const data = doc.data();
            posts.push({
              ...data,
              id: doc.id,
              dateLabel: moment
                .unix(data.timestamp?.seconds)
                .format('YYYY-MM-DD HH:mm:ss'),
            });
          });
          setForums(posts);
        }
      } catch (err) {
        console.error('Error fetching forums:', err);
      }
    };

    fetchData();
  }, [event.id, firebase, formatBarData, formatPieData]);

  // Capture charts as images once data is ready
  useEffect(() => {
    const captureCharts = async () => {
      if (!isDataReady) return;

      setCurrentStep(1);

      // Wait a bit for charts to render
      await new Promise((resolve) => setTimeout(resolve, 500));

      const images = { ...chartImages };

      // Capture bar charts
      if (citiesChartRef.current && citiesData.labels.length > 0) {
        images.c1 = citiesChartRef.current.toBase64Image();
      }

      if (countriesChartRef.current && countriesData.labels.length > 0) {
        images.c2 = countriesChartRef.current.toBase64Image();

        // Update country description after we have the chart
        setCountryDescription((prev) => ({
          ...prev,
          firstCountry: countriesData.labels[0],
          firstCity: citiesData.labels[0],
          secondCountries: countriesData.labels.slice(1, 3),
        }));
      }

      // Capture line chart
      if (hoursChartRef.current && hoursData.labels.length > 0) {
        images.c3 = hoursChartRef.current.toBase64Image();
      }

      // Capture map
      if (mapRef.current && Object.keys(countriesMap).length > 0) {
        try {
          const map = mapRef.current.getMapObject();
          if (map && map.canvas && map.canvas.node) {
            const serializer = new XMLSerializer();
            const svgString = serializer.serializeToString(map.canvas.node);

            const ctx = mapCanvasRef.current.getContext('2d');
            mapCanvasRef.current.width = 800;
            mapCanvasRef.current.height = 400;

            const canvg = await Canvg.from(ctx, svgString, {
              ignoreMouse: true,
              ignoreAnimation: true,
            });
            await canvg.render();

            images.c4 = mapCanvasRef.current.toDataURL('image/png');
          }
        } catch (err) {
          console.error('Error capturing map:', err);
        }
      }

      // Capture pie charts
      if (browsersChartRef.current && browsersData.labels.length > 0) {
        images.c7 = browsersChartRef.current.toBase64Image();
      }

      if (osChartRef.current && osData.labels.length > 0) {
        images.c8 = osChartRef.current.toBase64Image();
      }

      setChartImages(images);
      setCurrentStep(3);
      setIsChartsReady(true);
    };

    captureCharts();
  }, [isDataReady, citiesData, countriesData, hoursData, browsersData, osData, countriesMap]);

  // Error state
  if (error) {
    return (
      <div className="alert alert-danger mg-b-0" role="alert">
        <strong>Error:</strong> {error}
      </div>
    );
  }

  // No data state
  if (noData) {
    return (
      <div className="alert alert-danger mg-b-0" role="alert">
        <strong>Sin datos disponibles</strong>
        <p className="mb-0 mt-2">
          No hay datos disponibles para este evento. Por favor, intenta nuevamente
          una vez el evento haya finalizado.
        </p>
      </div>
    );
  }

  // Loading state - rendering charts for capture
  if (!isChartsReady) {
    const currentStepInfo = LOADING_STEPS[currentStep] || LOADING_STEPS[0];

    return (
      <div className="reportCoverLoading">
        <div className="report-overlay">
          <h3 className="text-center mb-3">
            <i className="fas fa-spinner fa-spin mr-2" />
            {currentStepInfo.label}...
          </h3>
          <div className="progress mb-3" style={{ height: '8px' }}>
            <div
              className="progress-bar bg-success"
              style={{ width: `${((currentStep + 1) / LOADING_STEPS.length) * 100}%` }}
            />
          </div>
          <p className="text-muted text-center small">
            Paso {currentStep + 1} de {LOADING_STEPS.length}
          </p>
        </div>

        {/* Hidden charts for capture */}
        <div style={{ position: 'absolute', left: '-9999px', width: '800px' }}>
          <div style={{ height: '400px' }}>
            <Bar
              ref={citiesChartRef}
              data={citiesData}
              options={barChartOptions}
            />
          </div>

          <div style={{ height: '400px' }}>
            <Bar
              ref={countriesChartRef}
              data={countriesData}
              options={barChartOptions}
            />
          </div>

          <div style={{ height: '300px' }}>
            <Line
              ref={hoursChartRef}
              data={hoursData}
              options={lineChartOptions}
            />
          </div>

          <ViewersMap theRef={mapRef} countries={countriesMap} />

          {/* Pie charts in square containers to maintain circular shape */}
          <div style={{ width: '400px', height: '300px' }}>
            <Pie
              ref={browsersChartRef}
              data={browsersData}
              options={pieChartOptions}
            />
          </div>

          <div style={{ width: '400px', height: '300px' }}>
            <Pie
              ref={osChartRef}
              data={osData}
              options={pieChartOptions}
            />
          </div>

          <canvas ref={mapCanvasRef} style={{ display: 'none' }} />
        </div>
      </div>
    );
  }

  // Document ready - show download link
  const documentProps = {
    event,
    currentUser,
    eventImage,
    totalUsers,
    totalConcurrences,
    charts: chartImages,
    countryDescription,
    countries: rawCountries,
    hoursDescription,
    chats,
    forums,
    onRender: () => setIsDocumentReady(true),
  };

  const fileName = `informe-${removeAccents(event.name)}_${moment().format('MMMM-DD-YYYY')}.pdf`;

  return (
    <ModulePanel title={['Informe de Audiencia y Conexiones']}>
      <div className="row">
        <div className="col-12 text-center py-4">
          {!isDocumentReady && (
            <div className="mb-3">
              <i className="fas fa-file-pdf fa-3x text-danger mb-3" />
              <h5>Preparando documento...</h5>
            </div>
          )}

          <PDFDownloadLink
            document={<Document {...documentProps} />}
            fileName={fileName}
            className="btn btn-success btn-lg"
          >
            {({ loading, error: pdfError }) => {
              if (pdfError) {
                return (
                  <span className="text-danger">
                    Error al generar el PDF
                  </span>
                );
              }

              if (loading) {
                return (
                  <span>
                    <i className="fas fa-spinner fa-spin mr-2" />
                    Generando PDF...
                  </span>
                );
              }

              return (
                <span>
                  <i className="fas fa-download mr-2" />
                  Descargar Informe PDF
                </span>
              );
            }}
          </PDFDownloadLink>

          <p className="text-muted mt-3 small">
            El informe incluye estadísticas de audiencia, distribución geográfica,
            análisis temporal y registro de conversaciones.
          </p>
        </div>
      </div>
    </ModulePanel>
  );
};

export default Report;
